import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Wind Chill: NWS 2001 formula
// F/mph:  35.74 + 0.6215T - 35.75V^0.16 + 0.4275T×V^0.16   (T<=50°F, V>3 mph)
// C/km-h: 13.12 + 0.6215T - 11.37V^0.16 + 0.3965T×V^0.16   (T<=10°C, V>4.8 km/h)

// Convert speed to mph (for imperial calc) and km/h (for metric calc)
const toMph = { mph:1, 'km/h':0.621371, 'm/s':2.23694, knots:1.15078 };
const toKmh = { mph:1.60934, 'km/h':1, 'm/s':3.6, knots:1.852 };

export default function WindChillCalc() {
  const [temp, setTemp] = useState(30);
  const [tUnit, setTUnit] = useState('F'); // F | C | K
  const [wind, setWind] = useState(15);
  const [wUnit, setWUnit] = useState('mph');

  // Normalise temp to F and C
  const toF = () => tUnit === 'F' ? Number(temp) : tUnit === 'K' ? (Number(temp) - 273.15)*9/5 + 32 : Number(temp)*9/5 + 32;
  const toC = () => tUnit === 'C' ? Number(temp) : tUnit === 'K' ? Number(temp) - 273.15 : (Number(temp) - 32)*5/9;

  const result = useMemo(() => {
    const F = toF(), C = toC();
    const vMph = Number(wind) * toMph[wUnit];
    const vKmh = Number(wind) * toKmh[wUnit];
    let chillF = null, chillC = null;
    if (F <= 50 && vMph > 3) chillF = 35.74 + 0.6215*F - 35.75*Math.pow(vMph, 0.16) + 0.4275*F*Math.pow(vMph, 0.16);
    if (C <= 10 && vKmh > 4.8) chillC = 13.12 + 0.6215*C - 11.37*Math.pow(vKmh, 0.16) + 0.3965*C*Math.pow(vKmh, 0.16);
    return { chillF, chillC };
  }, [temp, tUnit, wind, wUnit]);

  return (
    <CalcShell look="cnet" title="Wind Chill Calculator" subtitle="Compute apparent temperature (wind chill) from air temperature and wind speed.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Temperature</label>
            <div className="flex gap-2">
              <input type="number" value={temp} onChange={e => setTemp(e.target.value)} className="flex-1"/>
              <select value={tUnit} onChange={e => setTUnit(e.target.value)}><option value="F">°F — Fahrenheit</option><option value="C">°C — Celsius</option><option value="K">K — Kelvin</option></select>
            </div>
          </div>
          <div className="field"><label>Wind Speed</label>
            <div className="flex gap-2">
              <input type="number" value={wind} onChange={e => setWind(e.target.value)} className="flex-1"/>
              <select value={wUnit} onChange={e => setWUnit(e.target.value)}>{Object.keys(toMph).map(u => <option key={u}>{u}</option>)}</select>
            </div>
          </div>
        </div>
        <div>
          {result.chillF != null ? (
            <div className="result-panel"><div className="result-title">Wind Chill</div>
              <div className="result-value">{result.chillF.toFixed(1)}°F</div>
              <div className="text-sm text-slate-600 mt-1">{result.chillC?.toFixed(1)}°C · {(result.chillC + 273.15)?.toFixed(1)} K</div>
            </div>
          ) : <div className="text-orange-600 text-sm">Wind chill only applies for temperatures ≤ 50°F (10°C) and wind &gt; 3 mph (4.8 km/h).</div>}
        </div>
      </div>
    </CalcShell>
  );
}
